import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PurchaseContext = createContext();

const PURCHASE_STORAGE_KEY = 'digital_safety_purchase';
const TESTER_STORAGE_KEY = 'digital_safety_tester';

export function PurchaseProvider({ children }) {
  const [hasPurchased, setHasPurchased] = useState(() => {
    try {
      const saved = localStorage.getItem(PURCHASE_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.hasPurchased === true;
      }
    } catch (error) {
      console.error('Error loading purchase status:', error);
    }
    return false;
  });
  
  const [testerMode, setTesterMode] = useState(() => {
    try {
      const saved = localStorage.getItem(TESTER_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if not expired
        if (parsed.expiresAt) {
          const expiry = new Date(parsed.expiresAt);
          if (new Date() < expiry) {
            return {
              isActive: true,
              expiresAt: parsed.expiresAt
            };
          }
        }
      }
    } catch (error) {
      console.error('Error loading tester status:', error);
    }
    return { isActive: false, expiresAt: null };
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseDate, setPurchaseDate] = useState(null);

  // Check purchase and tester status from backend on mount
  useEffect(() => {
    checkAllStatus();
  }, []);

  const checkAllStatus = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Check purchase status
      const purchaseResponse = await fetch(`${backendUrl}/api/payments/purchase-status`);
      if (purchaseResponse.ok) {
        const purchaseData = await purchaseResponse.json();
        if (purchaseData.has_purchased) {
          setHasPurchased(true);
          setPurchaseDate(purchaseData.purchase_date);
          localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify({
            hasPurchased: true,
            purchaseDate: purchaseData.purchase_date
          }));
        }
      }
      
      // Check for manual access
      const accessResponse = await fetch(`${backendUrl}/api/access/check`);
      if (accessResponse.ok) {
        const accessData = await accessResponse.json();
        if (accessData.has_access && accessData.access_type === 'manual') {
          setHasPurchased(true);
          localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify({
            hasPurchased: true,
            accessType: 'manual'
          }));
        }
      }
      
      // Check tester status
      const testerResponse = await fetch(`${backendUrl}/api/tester/status`);
      if (testerResponse.ok) {
        const testerData = await testerResponse.json();
        if (testerData.is_tester && !testerData.is_expired) {
          setTesterMode({
            isActive: true,
            expiresAt: testerData.expires_at
          });
          localStorage.setItem(TESTER_STORAGE_KEY, JSON.stringify({
            isActive: true,
            expiresAt: testerData.expires_at
          }));
        } else if (testerData.is_expired) {
          // Clear expired tester mode
          setTesterMode({ isActive: false, expiresAt: null });
          localStorage.removeItem(TESTER_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const confirmPurchase = useCallback((date) => {
    setHasPurchased(true);
    setPurchaseDate(date || new Date().toISOString());
    localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify({
      hasPurchased: true,
      purchaseDate: date || new Date().toISOString()
    }));
    // Clear tester mode if purchased
    setTesterMode({ isActive: false, expiresAt: null });
    localStorage.removeItem(TESTER_STORAGE_KEY);
  }, []);

  const activateTesterMode = useCallback((expiresAt) => {
    setTesterMode({
      isActive: true,
      expiresAt: expiresAt
    });
    localStorage.setItem(TESTER_STORAGE_KEY, JSON.stringify({
      isActive: true,
      expiresAt: expiresAt
    }));
  }, []);

  const clearTesterMode = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/tester/clear`, { method: 'DELETE' });
    } catch (error) {
      console.error('Error clearing tester mode on backend:', error);
    }
    
    setTesterMode({ isActive: false, expiresAt: null });
    localStorage.removeItem(TESTER_STORAGE_KEY);
  }, []);

  const clearPurchase = useCallback(() => {
    setHasPurchased(false);
    setPurchaseDate(null);
    localStorage.removeItem(PURCHASE_STORAGE_KEY);
  }, []);

  // Computed: has access (either purchased or tester mode)
  const hasAccess = hasPurchased || testerMode.isActive;
  
  // Get formatted expiry date for tester mode
  const getTesterExpiryDate = useCallback(() => {
    if (!testerMode.expiresAt) return null;
    const date = new Date(testerMode.expiresAt);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }, [testerMode.expiresAt]);

  return (
    <PurchaseContext.Provider value={{
      hasPurchased,
      hasAccess,
      testerMode,
      isLoading,
      purchaseDate,
      checkPurchaseStatus: checkAllStatus,
      confirmPurchase,
      activateTesterMode,
      clearTesterMode,
      clearPurchase,
      getTesterExpiryDate,
    }}>
      {children}
    </PurchaseContext.Provider>
  );
}

export function usePurchase() {
  const context = useContext(PurchaseContext);
  if (!context) {
    throw new Error('usePurchase must be used within a PurchaseProvider');
  }
  return context;
}
